/** @module The user's tags: one cached list, and adding a new one. */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api, type components, problemFrom } from "@/api/client";

export type Tag = components["schemas"]["TagOut"];

async function fetchTags(): Promise<Tag[]> {
  const { data, response } = await api.GET("/api/v1/collection/tags");
  if (!data) throw problemFrom(response);
  return data;
}

export function useTags() {
  return useQuery({ queryKey: ["tags"], queryFn: fetchTags, staleTime: 5 * 60_000 });
}

export function useCreateTag() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (name: string): Promise<Tag> => {
      const { data, response } = await api.POST("/api/v1/collection/tags", {
        body: { name: name.trim() },
      });
      if (!data) throw problemFrom(response);
      return data;
    },
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ["tags"] });
    },
  });
}
